// Shared order-recommendation basis math for the feed-order boards (broiler +
// pig). This is the single source of truth for the "Order for [active]" tile.
//
// A current-month physical count is ground truth for "on hand now" and
// supersedes the previous-month ending ESTIMATE as the subtraction basis: the
// estimate (endOfPrevEst) is computed before the current-month count corrects
// the month, so subtracting it ignores the physical reality the operator just
// entered (the original bug — recommendations used the stale prev-month
// estimate even after a fresh count).
//
// actualOnHand is already count-aware: it folds in orders that arrived after
// the count, feed consumed since the count, and the "count includes current
// order" checkbox — so subtracting it never double-counts the current-month
// order. Because Need Thru [next] is dynamic (remaining current-month need +
// next-month projected need, after subtracting feed already consumed in the
// active month), both Need Thru [next] and Actual On Hand are aligned as of
// today. When there is no current-month count, the previous-month ending
// estimate is brought forward to today by subtracting activeConsumed.

// Both lbs inputs are nullable on purpose: `actualOnHand` is null until a
// current-month physical count exists, and `endOfPrevEst` is null for the very
// first month of a program's ledger. That nullability IS the branch below.
export type FeedOrderBasisInput = {
  hasCurrentCount: boolean;
  actualOnHand: number | null | undefined;
  endOfPrevEst: number | null | undefined;
  activeConsumed?: number | null | undefined;
};

// Dynamic remaining 2-month need from today through the end of next month:
// remaining current-month demand (floored at 0 if actuals exceed projection)
// plus full next-month projected demand.
export function remainingNeedThruNext({
  activeProjected,
  activeConsumed = 0,
  nextProjected,
}: {
  activeProjected: number;
  activeConsumed?: number | null | undefined;
  nextProjected: number;
}): number {
  const consumed = Math.max(0, Math.round(activeConsumed || 0));
  const activeRemaining = Math.max(0, Math.round(activeProjected || 0) - consumed);
  return activeRemaining + Math.round(nextProjected || 0);
}

// The lbs value the recommendation subtracts from Need-Thru-next.
export function feedOrderBasis({
  hasCurrentCount,
  actualOnHand,
  endOfPrevEst,
  activeConsumed = 0,
}: FeedOrderBasisInput): number | null | undefined {
  if (hasCurrentCount && actualOnHand != null) {
    return actualOnHand;
  }
  if (endOfPrevEst == null) return endOfPrevEst;
  return endOfPrevEst - Math.max(0, Math.round(activeConsumed || 0));
}

// Recommended order = max(0, Need Thru next − basis). Returns null when no
// basis is available (no count and no previous-month ledger end).
export function recommendedFeedOrder({
  needThruNext,
  hasCurrentCount,
  actualOnHand,
  endOfPrevEst,
  activeConsumed = 0,
}: FeedOrderBasisInput & {needThruNext: number}): number | null {
  const basis = feedOrderBasis({hasCurrentCount, actualOnHand, endOfPrevEst, activeConsumed});
  if (basis == null) return null;
  return Math.max(0, needThruNext - basis);
}

// Projected surplus (+) or deficit (-) through the end of next month:
//   basis (Actual On Hand when counted this month, else End of Prev Est. minus
//   activeConsumed) + activeOrdered − dynamic Need Thru [next].
// Returns null when no basis is available.
export function thruNextDelta({
  needThruNext,
  hasCurrentCount,
  actualOnHand,
  endOfPrevEst,
  activeConsumed = 0,
  activeOrdered = 0,
}: FeedOrderBasisInput & {
  needThruNext: number;
  activeOrdered?: number | null | undefined;
}): number | null {
  const basis = feedOrderBasis({hasCurrentCount, actualOnHand, endOfPrevEst, activeConsumed});
  if (basis == null) return null;
  const ordered = Math.max(0, Math.round(activeOrdered || 0));
  return Math.round(basis + ordered - needThruNext);
}

// A calendar month key, 'YYYY-MM'. Typed as plain `string` rather than a
// template-literal type: these values come out of database columns and user
// selections, and a template-literal type would be a claim nothing validates.
export type YearMonth = string;

export function addMonthsYM(ym: YearMonth, delta: number): YearMonth {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

export function ymFromDate(date: Date | string | number = new Date()): YearMonth {
  const d = date instanceof Date ? date : new Date(date);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

// The feed-order board is pinned to the current calendar month, not "next
// month" or "first unsaved".
// Example: any day in Jul 2026 shows the Jul 2026 order card. Saving Jul
// does not hide it; the card stays editable until the calendar flips to Aug.
export function calendarOrderYM(today: Date | string | number = new Date()): YearMonth {
  return ymFromDate(today);
}
